// 📄 components/layout/OrgSwitcher.tsx
// Sub-fase G1: selector de organización activa en la topbar. Clona el
// patrón visual/estructural de UserMenu.tsx (dropdown con click-outside).
// Cambiar de org acá SOLO actualiza el estado en AuthContext (+
// localStorage) — no navega ni refresca nada (los efectos reales sobre
// Mis Proyectos/Clientes llegan en Sub-fase G2).
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Building2, User as UserIcon, Users as UsersIcon, Check, Settings, Plus, Pencil, Archive } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import GestionarMiembrosModal from '../organizacion/GestionarMiembrosModal.tsx';
import CreateTeamOrgModal from '../organizacion/CreateTeamOrgModal.tsx';
import EditarOrgModal from '../organizacion/EditarOrgModal.tsx';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  miembro: 'Miembro',
  invitado: 'Invitado'
};

export default function OrgSwitcher() {
  const { user, organizations, orgActual, orgsLoading, setOrgActual, reloadOrganizations } = useAuth();
  const [open, setOpen] = useState(false);
  const [gestionarAbierto, setGestionarAbierto] = useState(false);
  const [crearAbierto, setCrearAbierto] = useState(false);
  const [editarAbierto, setEditarAbierto] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  // Mientras carga (o si por algún motivo no hay ninguna org resuelta
  // todavía) no mostramos nada — evita un flash de "sin organización"
  // antes de que AuthContext termine de resolver la personal por default.
  if (orgsLoading && organizations.length === 0) return null;
  if (!orgActual) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full pl-3 pr-2.5 py-1.5 transition-all"
        title="Cambiar organización activa"
      >
        {orgActual.type === 'personal' ? (
          <UserIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        ) : (
          <UsersIcon className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
        )}
        <span className="text-sm text-gray-200 max-w-[160px] truncate">{orgActual.name}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-gray-800 border border-white/10 rounded-xl shadow-lg overflow-hidden z-50">
          <div className="px-4 py-2.5 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-gray-500" />
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Tus organizaciones</p>
          </div>

          <div className="border-t border-white/10" />

          {organizations.map((org) => {
            const isActiva = org.id === orgActual.id;
            return (
              <button
                key={org.id}
                onClick={() => { setOrgActual(org.id); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-all ${
                  isActiva ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                {org.type === 'personal' ? (
                  <UserIcon className="w-4 h-4 flex-shrink-0 text-gray-500" />
                ) : (
                  <UsersIcon className="w-4 h-4 flex-shrink-0 text-indigo-400" />
                )}
                <span className="flex-1 truncate">{org.name}</span>
                {org.archivada && (
                  <span className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0" title="Archivada — no admite proyectos ni miembros nuevos">
                    <Archive className="w-3 h-3" /> Archivada
                  </span>
                )}
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0">
                  {ROLE_LABELS[org.role] || org.role}
                </span>
                {isActiva && <Check className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />}
              </button>
            );
          })}

          <div className="border-t border-white/10" />

          <button
            onClick={() => { setGestionarAbierto(true); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left text-gray-300 hover:bg-white/5 transition-all"
          >
            <Settings className="w-4 h-4 flex-shrink-0 text-gray-500" />
            <span>Gestionar miembros de {orgActual.name}</span>
          </button>

          {/* Renombrar/archivar (2026-08-20) — solo el OWNER de un equipo,
              acá porque OrganizationsSection.tsx (misma funcionalidad)
              cuelga de /admin y un usuario normal no llega ahí. */}
          {orgActual.role === 'owner' && orgActual.type === 'equipo' && (
            <button
              onClick={() => { setEditarAbierto(true); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left text-gray-300 hover:bg-white/5 transition-all"
            >
              <Pencil className="w-4 h-4 flex-shrink-0 text-gray-500" />
              <span>Editar {orgActual.name}</span>
            </button>
          )}

          <button
            onClick={() => { setCrearAbierto(true); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left text-gray-300 hover:bg-white/5 transition-all"
          >
            <Plus className="w-4 h-4 flex-shrink-0 text-gray-500" />
            <span>Crear equipo</span>
          </button>
        </div>
      )}

      {gestionarAbierto && (
        <GestionarMiembrosModal
          orgId={orgActual.id}
          orgNombre={orgActual.name}
          orgType={orgActual.type}
          onClose={() => setGestionarAbierto(false)}
        />
      )}

      {crearAbierto && (
        <CreateTeamOrgModal
          onClose={() => setCrearAbierto(false)}
          onCreated={() => { setCrearAbierto(false); reloadOrganizations(); }}
        />
      )}

      {editarAbierto && (
        <EditarOrgModal
          orgId={orgActual.id}
          orgNombre={orgActual.name}
          archivada={orgActual.archivada}
          onClose={() => setEditarAbierto(false)}
          onSaved={() => { setEditarAbierto(false); reloadOrganizations(); }}
        />
      )}
    </div>
  );
}
